/**
 * AI CONTEXT
 * Purpose: settings hub with permission-driven tabs (tags/semesters/components).
 * Key deps: useResourcePermissions/useHasPermission, TagsTable, SemesterCalendarSettings.
 * Referenced by: App route ROUTES.SETTINGS.
 * Importance: central config surface for low-frequency high-impact data.
 * Refactor: derive default tab from first allowed tab; lazy-load heavy test tab.
 */
import { Tabs } from "antd"
import { useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import AntdThemeTest from "#components/AntdComponentTest/AntdThemeTest"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { EnumValues } from "#customTypes/types"
import useHasPermission, {
  useResourcePermissions,
} from "#hooks/newHooks/useHasPermission"

import SemesterCalendarSettings from "./SemesterCalendarSettings"
import TagsTable from "./TagsTable"

const TABS = {
  TAGS: "tags",
  SEMESTERS: "semesters",
  TEST: "test",
  COMPONENTS: "components",
} as const

const Settings = () => {
  const [activeTab, setActiveTab] = useState<EnumValues<typeof TABS>>(TABS.TAGS)

  const { canRead } = useResourcePermissions(RESOURCES.SETTINGS)

  const canReadTags = useHasPermission(RESOURCES.TAG, USER_ACTIONS.READ)
  const canReadSemesters = useHasPermission(
    RESOURCES.SEMESTER,
    USER_ACTIONS.READ
  )
  const canReadTest = useHasPermission(RESOURCES.TEST, USER_ACTIONS.READ)
  const items = []

  if (!canRead) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  if (canReadTags) {
    items.push({ label: "Tags", key: TABS.TAGS })
  }
  if (canReadSemesters) {
    items.push({ label: "Semesters", key: TABS.SEMESTERS })
  }
  if (canReadTest) {
    items.push({ label: "Components", key: TABS.COMPONENTS })
  }

  return (
    <Page>
      <Flex
        col
        style={{ height: "100%", width: "100%", padding: 20, minHeight: 0 }}
        gap={12}
      >
        <Flex col center gap={10} style={{ flex: "0 0 auto" }}>
          <h1>Settings</h1>
          <Tabs
            size="large"
            items={items}
            onChange={(key) => setActiveTab(key as EnumValues<typeof TABS>)}
          />
        </Flex>
        <Flex
          col
          style={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}
          gap={10}
        >
          {canReadTags && activeTab === TABS.TAGS && <TagsTable />}
          {canReadSemesters && activeTab === TABS.SEMESTERS && (
            <SemesterCalendarSettings />
          )}
          {canReadTest && activeTab === TABS.COMPONENTS && <AntdThemeTest />}
        </Flex>
      </Flex>
    </Page>
  )
}

export default Settings
